import { faCircle, faWarning } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { InclusionStatus } from "@/enum/list.enum";

import { RenderOverallScore } from "./RenderOverallScore";
import { isLeadBlacklisted, isLeadUnsubscribed } from "./isLeadBlacklisted";
import { ScoringPendingBadge } from "./ScoringPendingBadge";
import { useIsScoringPending } from "../hooks/useScoringPending";
import { ListPerson } from "../schema/list";

const INCLUSION_STATUS_COLOR = "#22AD5C";

const dotColor = (inclusionStatus?: string | null) => {
	if (inclusionStatus === InclusionStatus.NOT_FOUND) {
		return "#FF9900";
	}

	if (inclusionStatus === InclusionStatus.UNSURE) {
		return "#F23030";
	}

	return INCLUSION_STATUS_COLOR;
};

/**
 * The left-locked status indicator of one lead, in priority order:
 *
 * 1. blacklisted / unsubscribed — an explicit warning that must never be hidden by a score
 *    (PREC-3728);
 * 2. lead scoring queued — the turning badge, which deliberately replaces an existing score,
 *    because during a re-score the score on screen is the one being thrown away (PREC-4076).
 *    It also covers a lead being scored for the first time during a list build, which used to
 *    show nothing at all;
 * 3. a scoring result — the P1/P2/P3/DQ pill;
 * 4. otherwise a small dot for the inclusion status.
 */
export const LeadStatusCell = ({ row, ts }: { row: ListPerson; ts?: (key: string) => string }) => {
	const isScoringPending = useIsScoringPending(row?.list?.id, row?.id);

	if (isLeadBlacklisted(row)) {
		const warningLabel = isLeadUnsubscribed(row)
			? ts?.("blacklistWarning.unsubscribed")
			: ts?.("blacklistWarning.blacklisted");

		return (
			<div className="relative flex justify-center items-center">
				<Tooltip content={warningLabel}>
					<Icon className="h-[12px]" color="#F23030" icon={faWarning} />
				</Tooltip>
			</div>
		);
	}

	const hasScoringResult =
		!!row.prospect?.organization?.scoreCompanyCategoryResult ||
		!!row.prospect?.scorePersonaCategoryResult;

	return (
		<div className="relative flex justify-center items-center">
			{isScoringPending ? (
				<ScoringPendingBadge label={ts?.("leadScoringInProgress")} />
			) : hasScoringResult ? (
				<RenderOverallScore
					companyCategory={row.prospect?.organization?.scoreCompanyCategoryResult}
					listPerson={row}
					scoring={row.prospect?.scorePersonaCategoryResult}
				/>
			) : (
				<Icon
					className="h-[6px] opacity-[0.5]"
					color={dotColor(row.inclusionStatus)}
					icon={faCircle}
				/>
			)}
		</div>
	);
};
